import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { Building2, Plus, Edit, Trash2, RefreshCw, Key, Users, Calendar, AlertTriangle, CheckCircle, Clock, Ban, Search, LogOut, ExternalLink, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SuperAdminDashboard = () => {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [accessingClub, setAccessingClub] = useState(null);  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubUsers, setClubUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [promotingUser, setPromotingUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    siret: ''
  });
  const [renewYears, setRenewYears] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, clubsRes] = await Promise.all([
        axios.get(`${API_URL}/api/superadmin/stats`),
        axios.get(`${API_URL}/api/superadmin/clubs`)
      ]);
      setStats(statsRes.data);
      setClubs(clubsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateClub = async () => {
    if (!formData.nom || !formData.siret) {
      toast.error("Le nom et le SIRET sont obligatoires");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/superadmin/clubs`, formData);
      setClubs([response.data, ...clubs]);
      setCreateDialogOpen(false);
      setFormData({ nom: '', adresse: '', code_postal: '', ville: '', siret: '' });
      toast.success(`Club créé - Code licence: ${response.data.licence_code}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de créer le club");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateClub = async () => {
    if (!selectedClub) return;
    
    setSubmitting(true);
    try {
      const response = await axios.put(`${API_URL}/api/superadmin/clubs/${selectedClub.id}`, formData);
      setClubs(clubs.map(c => c.id === selectedClub.id ? response.data : c));
      setEditDialogOpen(false);
      setSelectedClub(null);
      toast.success("Club mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de mettre à jour");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClub = async () => {
    if (!selectedClub) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/superadmin/clubs/${selectedClub.id}`);
      setClubs(clubs.filter(c => c.id !== selectedClub.id));
      setDeleteDialogOpen(false);
      setSelectedClub(null);
      toast.success("Club supprimé");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de supprimer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenewLicence = async () => {
    if (!selectedClub) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/superadmin/clubs/${selectedClub.id}/renew-licence?years=${renewYears}`
      );
      setClubs(clubs.map(c => c.id === selectedClub.id ? response.data : c));
      setRenewDialogOpen(false);
      setSelectedClub(null);
      toast.success(`Licence renouvelée - +${renewYears} an(s)`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de renouveler");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (club) => {
    setSelectedClub(club);
    setFormData({
      nom: club.nom || '',
      adresse: club.adresse || '',
      code_postal: club.code_postal || '',
      ville: club.ville || '',
      siret: club.siret || ''
    });
    setEditDialogOpen(true);
  };

  const handleAccessClub = async (club) => {
    if (club.statut !== 'actif') {
      toast.error("Ce club n'est pas encore actif");
      return;
    }
    
    setAccessingClub(club.id);
    try {
      const response = await axios.post(`${API_URL}/api/superadmin/clubs/${club.id}/access`);
      
      // Store the impersonation token and user data
      // We use localStorage directly because setUser is async and navigate would run before state updates
      localStorage.setItem('equibook_token', response.data.token);
      localStorage.setItem('equibook_impersonating_user', JSON.stringify(response.data.user));
      
      toast.success(`Accès au club ${club.nom}`);
      
      // Force full page reload to ensure new token is used
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error accessing club:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'accès au club");
      setAccessingClub(null);
    }
  };

  const handleViewUsers = async (club) => {
    setSelectedClub(club);
    setUsersDialogOpen(true);
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/api/superadmin/clubs/${club.id}/users`);
      setClubUsers(response.data.users || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
      setClubUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handlePromoteUser = async (user) => {
    if (!selectedClub) return;
    
    setPromotingUser(user.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/superadmin/clubs/${selectedClub.id}/users/${user.id}/promote-admin`
      );
      
      // Show success message
      toast.success(response.data.message);
      
      // Show additional info about email
      if (response.data.activation_email_sent) {
        toast.info(`Email d'activation envoyé à ${response.data.user_email}`);
      }
      
      // Refresh users list
      const usersResponse = await axios.get(`${API_URL}/api/superadmin/clubs/${selectedClub.id}/users`);
      setClubUsers(usersResponse.data.users || []);
      
      // Refresh clubs list if club was activated
      if (response.data.club_activated) {
        toast.success(`Club "${selectedClub.nom}" activé !`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la promotion");
    } finally {
      setPromotingUser(null);
    }
  };

  const getStatusBadge = (club) => {
    const statut = club.statut;
    if (statut === 'actif') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Actif</Badge>;
    } else if (statut === 'en_attente') {
      return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    } else if (statut === 'suspendu') {
      return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="w-3 h-3 mr-1" />Suspendu</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800"><Ban className="w-3 h-3 mr-1" />Bloqué</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const filteredClubs = clubs.filter(club =>
    club.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    club.siret?.includes(searchTerm) ||
    club.ville?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-[#2D5A3D]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Super Admin</h1>
                <p className="text-sm text-gray-500">Gestion des clubs Equibook</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Clubs</CardDescription>
              <CardTitle className="text-3xl">{stats?.total_clubs || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Clubs Actifs</CardDescription>
              <CardTitle className="text-3xl text-green-600">{stats?.active_clubs || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>En Attente</CardDescription>
              <CardTitle className="text-3xl text-yellow-600">{stats?.pending_clubs || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Suspendus</CardDescription>
              <CardTitle className="text-3xl text-red-600">{stats?.suspended_clubs || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Expire bientôt</CardDescription>
              <CardTitle className="text-3xl text-orange-600">{stats?.expiring_soon || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Expiring Soon Alert */}
        {stats?.expiring_soon > 0 && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-800">
                  {stats.expiring_soon} club{stats.expiring_soon > 1 ? 's' : ''} expire{stats.expiring_soon > 1 ? 'nt' : ''} dans les 30 prochains jours
                </p>
                <p className="text-sm text-orange-600">Envoyez des rappels aux administrateurs concernés</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-orange-600 text-orange-600 hover:bg-orange-100"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API_URL}/api/superadmin/clubs/send-expiration-warnings?days=30`);
                  toast.success(res.data.message);
                } catch (err) {
                  toast.error("Erreur lors de l'envoi des alertes");
                }
              }}
            >
              Envoyer les alertes
            </Button>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Rechercher par nom, SIRET ou ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/super-admin/stats')}
              data-testid="open-stats-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiques
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/super-admin/activity-logs')}
              data-testid="open-logs-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              Journal d'activité
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/super-admin/raz')}
              data-testid="open-raz-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Administration RAZ
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#2D5A3D] hover:bg-[#234830]">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Club
            </Button>
          </div>
        </div>

        {/* Clubs List */}
        <Card>
          <CardHeader>
            <CardTitle>Clubs ({filteredClubs.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Club</th>
                    <th className="pb-3 font-medium">SIRET</th>
                    <th className="pb-3 font-medium">Statut</th>
                    <th className="pb-3 font-medium">Licence</th>
                    <th className="pb-3 font-medium">Expiration</th>
                    <th className="pb-3 font-medium">Installation</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClubs.map((club) => (
                    <tr key={club.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-4">
                        <div>
                          <p className="font-medium text-gray-900">{club.nom}</p>
                          <p className="text-sm text-gray-500">
                            {[club.adresse, club.code_postal, club.ville].filter(Boolean).join(', ') || 'Adresse non renseignée'}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 text-sm text-gray-600 font-mono">{club.siret}</td>
                      <td className="py-4">{getStatusBadge(club)}</td>
                      <td className="py-4">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                          {club.licence_code}
                        </code>
                      </td>
                      <td className="py-4 text-sm text-gray-600">
                        {formatDate(club.licence_expiration)}
                      </td>
                      <td className="py-4">
                        {club.installed ? (
                          <span className="text-sm text-green-600">✓ {formatDate(club.installed_at)}</span>
                        ) : (
                          <span className="text-sm text-gray-400">Non installé</span>
                        )}
                      </td>
                      <td className="py-4">
                        <div className="flex justify-end gap-1">
                          {/* Bouton Voir utilisateurs - toujours visible pour les clubs en attente */}
                          {club.statut === 'en_attente' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewUsers(club)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Voir utilisateurs (activer admin)"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Bouton Accès au site - visible pour les clubs actifs */}
                          {club.statut === 'actif' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleAccessClub(club)}
                              disabled={accessingClub === club.id}
                              className="text-[#2D5A3D] hover:text-[#234830] hover:bg-green-50"
                              title="Accès au site"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(club)}
                            title="Modifier"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedClub(club); setRenewDialogOpen(true); }}
                            title="Renouveler licence"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedClub(club); setDeleteDialogOpen(true); }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredClubs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="py-8 text-center text-gray-500">
                        {searchTerm ? 'Aucun club trouvé' : 'Aucun club créé'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Club Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Créer un nouveau club</DialogTitle>
            <DialogDescription>
              Un code de licence sera généré automatiquement.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="nom">Nom du club *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                placeholder="Club Équestre du Soleil"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="siret">SIRET *</Label>
              <Input
                id="siret"
                value={formData.siret}
                onChange={(e) => setFormData({...formData, siret: e.target.value})}
                placeholder="12345678901234"
                maxLength={14}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="adresse">Adresse</Label>
              <Input
                id="adresse"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                placeholder="123 Route des Chevaux"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="code_postal">Code postal</Label>
                <Input
                  id="code_postal"
                  value={formData.code_postal}
                  onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                  placeholder="75001"
                  maxLength={5}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ville">Ville</Label>
                <Input
                  id="ville"
                  value={formData.ville}
                  onChange={(e) => setFormData({...formData, ville: e.target.value})}
                  placeholder="Paris"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateClub} disabled={submitting} className="bg-[#2D5A3D] hover:bg-[#234830]">
              {submitting ? 'Création...' : 'Créer le club'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Club Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Modifier le club</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-nom">Nom du club</Label>
              <Input
                id="edit-nom"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-adresse">Adresse</Label>
              <Input
                id="edit-adresse"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-code_postal">Code postal</Label>
                <Input
                  id="edit-code_postal"
                  value={formData.code_postal}
                  onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-ville">Ville</Label>
                <Input
                  id="edit-ville"
                  value={formData.ville}
                  onChange={(e) => setFormData({...formData, ville: e.target.value})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleUpdateClub} disabled={submitting} className="bg-[#2D5A3D] hover:bg-[#234830]">
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le club ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les données du club seront supprimées :
              utilisateurs, chevaux, cours, factures, etc.
              <br /><br />
              <strong>Club : {selectedClub?.nom}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClub}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Suppression...' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renew Licence Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Renouveler la licence</DialogTitle>
            <DialogDescription>
              Club : {selectedClub?.nom}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Durée du renouvellement</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 5].map((years) => (
                  <Button
                    key={years}
                    variant={renewYears === years ? 'default' : 'outline'}
                    onClick={() => setRenewYears(years)}
                    className={renewYears === years ? 'bg-[#2D5A3D]' : ''}
                  >
                    {years} an{years > 1 ? 's' : ''}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Expiration actuelle : {formatDate(selectedClub?.licence_expiration)}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleRenewLicence} disabled={submitting} className="bg-[#2D5A3D] hover:bg-[#234830]">
              {submitting ? 'Renouvellement...' : 'Renouveler'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Users Dialog for pending clubs */}
      <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Utilisateurs du club {selectedClub?.nom}</DialogTitle>
            <DialogDescription>
              {selectedClub?.statut === 'en_attente' && (
                <span className="text-amber-600">
                  Ce club est en attente d'activation. Promouvoir un utilisateur en administrateur activera le club.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {loadingUsers ? (
              <div className="text-center py-8">
                <div className="spinner mx-auto mb-2" />
                Chargement des utilisateurs...
              </div>
            ) : clubUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucun utilisateur inscrit dans ce club
              </div>
            ) : (
              <div className="space-y-3">
                {clubUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{user.prenom} {user.nom}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {user.role_club === 'instructeur_admin' ? 'Admin' : 
                           user.role_club === 'instructeur' ? 'Instructeur' : 'Cavalier'}
                        </Badge>
                        {user.is_approved === 'approuve' ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Approuvé</Badge>
                        ) : user.is_approved === 'en_attente' ? (
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs">En attente</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 text-xs">Refusé</Badge>
                        )}
                      </div>
                    </div>
                    {user.role_club !== 'instructeur_admin' && user.is_approved !== 'approuve' && (
                      <Button
                        size="sm"
                        onClick={() => handlePromoteUser(user)}
                        disabled={promotingUser === user.id}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {promotingUser === user.id ? 'Promotion...' : 'Promouvoir Admin'}
                      </Button>
                    )}
                    {user.role_club === 'instructeur_admin' && user.is_approved === 'approuve' && (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Administrateur actif
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsersDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
